// =============================================================================
// Service image registry — Black Ridge Construction
// =============================================================================
//
// Each constant in `BR` is a NAMED ROLE (e.g. "roofRepairActive"), and the
// value is the public CDN URL that actually contains that subject. The URLs
// in this registry were verified against the photos via image analysis so
// the named role and the URL always match.
//
// Mapping rules (per brand spec):
//   1. Real, owner-supplied BR project / jobsite imagery is the FIRST choice.
//   2. Premium licensed stock (Unsplash / Pexels) is used as a fallback only
//      for services that don't yet have a matching BR upload.
//   3. Visual treatment (16:9 crop + dark overlay for text) is enforced in
//      <ServiceCard /> and <ServiceDetail />.
// =============================================================================

// --------------------------------------------------------------------------
// BR — Owner-supplied Black Ridge Construction project / jobsite imagery.
// All URLs verified against actual content via image analysis. Keep this
// block strictly in role → URL form so swapping a single photo only
// requires editing one line.
// --------------------------------------------------------------------------
const BR = {
  // ---------------------------------------------------------------------
  // Roofing — damage / problem close-ups (iStock product shots)
  // ---------------------------------------------------------------------
  shinglesAging:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/ygzozo3h_istockphoto-925998396-612x612.jpg",
  shinglesTornRidge:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/3k4dfav8_istockphoto-1331208030-612x612.jpg",
  shinglesLifted:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/p0hio2iv_istockphoto-1331208037-612x612.jpg",
  roofGutterChimney:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/r8yi53wh_istockphoto-1349092401-612x612.jpg",

  // ---------------------------------------------------------------------
  // Roofing — active install / repair (BR jobsite photos)
  // ---------------------------------------------------------------------
  // Two roofers actively repairing shingles with full harness + ropes.
  roofRepairActive:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/h73oo0xp_raze-solar-RGkNFjRPyO0-unsplash.jpg",
  // Roofers installing with ABC Pro Guard underlayment + REVIT synthetic shingles.
  roofInstallSlate:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/n8j8v08h_pexels-rstephens-33404080.jpg",
  // Foreground close-up of a roofer with nail gun applying shingles over underlayment.
  roofInstallCloseup:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/p62ex88s_pexels-rstephens-33404248.jpg",

  // ---------------------------------------------------------------------
  // Roofing — tear-offs / large replacements (BR jobsite photos)
  // ---------------------------------------------------------------------
  // Wide tear-off + replacement crew on a 2-story brick home.
  roofTearoffBrick:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/qv80jjho_pexels-rstephens-33404981.jpg",
  // Large stone + brick home mid tear-off with boarded windows (storm-restoration look).
  roofTearoffStone:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/cs9bzr48_pexels-rstephens-34304714.jpg",

  // ---------------------------------------------------------------------
  // Roofing — inspection / RoofWatch
  // ---------------------------------------------------------------------
  // Roofer on a sloped shingled roof with safety harness (clearing debris).
  roofInspectionWalk:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/kmd0dsi8_raze-solar-G8tyblQ4p04-unsplash.jpg",

  // ---------------------------------------------------------------------
  // Kitchens (Curtis Adams interior photography + one rstephens premium build)
  // ---------------------------------------------------------------------
  // White + slate-blue island kitchen, marble, brass hardware, Sub-Zero.
  kitchenWhiteBlue:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/8mwrp40e_pexels-rstephens-37677516.jpg",
  kitchenGrayBrass:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/ipfi22cn_pexels-curtis-adams-1694007-4800175.jpg",
  kitchenGalleyGray:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/aoe1zxy0_pexels-curtis-adams-1694007-4800177.jpg",
  kitchenGrayStools:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/zys7t54y_pexels-curtis-adams-1694007-4800189.jpg",
  kitchenTwoTone:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/5vzvsftq_pexels-curtis-adams-1694007-19227214.jpg",

  // ---------------------------------------------------------------------
  // Bathrooms (Curtis Adams)
  // ---------------------------------------------------------------------
  bathMarbleShiplap:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/vj2kc3lp_pexels-curtis-adams-1694007-15062118.jpg",
  bathTubCloseup:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/f6du06bd_pexels-curtis-adams-1694007-15668077.jpg",
  bathCompactCorner:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/8tvra1dg_pexels-curtis-adams-1694007-15668078.jpg",

  // ---------------------------------------------------------------------
  // Interior feature spaces
  // ---------------------------------------------------------------------
  interiorStaircaseFireplace:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/9oi4z5rk_pexels-art-rutka-109215-16581570.jpg",

  // ---------------------------------------------------------------------
  // Outdoor living
  // ---------------------------------------------------------------------
  // Finished backyard with pool, spa, covered patio, artificial turf.
  outdoorPoolPergola:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/zwsz1jx8_pexels-rstephens-33404118.jpg",
  // Outdoor kitchen under construction — concrete blocks + stone facade + wooden framing.
  outdoorKitchenBuild:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/e1pdus0r_pexels-rstephens-33494726.jpg",
  // Finished outdoor kitchen — stone-clad counter, grill, fridge, sink, pergola.
  outdoorKitchenFinished:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/b29l1vws_pexels-rstephens-33505327.jpg",

  // ---------------------------------------------------------------------
  // Framing / additions
  // ---------------------------------------------------------------------
  // Interior wood framing — exposed studs, joists, subfloor, worker present.
  framingInterior:
    "https://customer-assets.emergentagent.com/job_ridge-roofing-austin/artifacts/rqsghcxu_pexels-rstephens-33405084.jpg",
};

// --------------------------------------------------------------------------
// STOCK — Premium licensed fallbacks for service categories that don't yet
// have a BR-supplied image. Sourced from Unsplash / Pexels and chosen to
// stay tonally cohesive (neutral, modern, residential).
// --------------------------------------------------------------------------
const STOCK = {
  paintRoller:
    "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=1600&q=80",
  paintWall:
    "https://images.pexels.com/photos/5797991/pexels-photo-5797991.jpeg?w=1600",
  painterMask:
    "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1600&q=80",
  caulkGun:
    "https://images.unsplash.com/photo-1607400201515-c2c41c07d307?w=1600&q=80",
  trimMirror:
    "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=1600&q=80",
  homeCraftsman:
    "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=1600&q=80",
  homeSunset:
    "https://images.unsplash.com/photo-1564540583246-934409427776?w=1600&q=80",
  homeColonial:
    "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1600&q=80",
  homeGarageVilla:
    "https://images.pexels.com/photos/210602/pexels-photo-210602.jpeg?w=1600",
  workersHardhat:
    "https://images.unsplash.com/photo-1626885930974-4b69aa21bbf9?w=1600&q=80",
};

// =============================================================================
// SLUG → IMAGE MAP
// =============================================================================
// Tags: [BR] = Black Ridge supplied. [STOCK – needs BR replacement] = licensed
// stock placeholder. [TEMP] = BR image used as a temporary stand-in for a
// related service until we have a dedicated photo.
// =============================================================================
export const SERVICE_IMAGES = {
  // ----- Roofing ---------------------------------------------------------
  "roof-repair": BR.roofRepairActive,             // [BR] two roofers actively working
  "roof-replacement": BR.roofInstallSlate,        // [BR] full install in progress
  "storm-damage-roofing": BR.shinglesLifted,      // [BR] lifted shingles close-up
  "storm-damage-repairs": BR.roofTearoffBrick,    // [BR] brick home tear-off jobsite
  "gutter-drainage-repairs": BR.roofGutterChimney, // [BR] chimney + gutter detail
  "fascia-soffit-repair": BR.roofGutterChimney,   // [BR] roof edge + soffit/gutter visible

  // ----- Remodeling ------------------------------------------------------
  "kitchen-remodeling": BR.kitchenWhiteBlue,      // [BR] white + slate-blue marble kitchen
  "bathroom-remodeling": BR.bathCompactCorner,    // [BR] compact bath w/ glass corner shower
  "full-home-remodeling": BR.kitchenGrayStools,   // [BR] wide premium remodel scene
  "interior-feature-upgrades": BR.interiorStaircaseFireplace, // [BR] fireplace + feature wall
  "flooring-installation": BR.kitchenTwoTone,     // [BR] two-tone kitchen w/ wood floor visible
  "interior-painting": BR.bathMarbleShiplap,      // [BR-TEMP] clean white interior
  "trim-carpentry": BR.interiorStaircaseFireplace, // [BR-TEMP] trim + feature detail

  // ----- Additions & outdoor --------------------------------------------
  "home-additions": BR.framingInterior,           // [BR] interior wood framing
  "adu-construction": BR.framingInterior,         // [BR-TEMP] framing — need ADU exterior
  "garage-conversions": BR.framingInterior,       // [BR-TEMP] framing — need finished garage space
  "outdoor-living": BR.outdoorPoolPergola,        // [BR] finished backyard pool + pergola
  "deck-building": BR.outdoorPoolPergola,         // [BR-TEMP] outdoor living — need deck-specific
  "patio-covers": BR.outdoorKitchenFinished,      // [BR] finished covered outdoor kitchen
  "pergolas": BR.outdoorKitchenFinished,          // [BR-TEMP] covered structure — need pergola-specific

  // ----- Repairs & exterior ---------------------------------------------
  "exterior-repairs": BR.roofTearoffStone,        // [BR] large stone home jobsite
  "siding-trim-repairs": BR.shinglesAging,        // [BR-TEMP] aging shingles + roofline
  "drywall-painting": BR.bathMarbleShiplap,       // [BR-TEMP] clean interior
  "fence-gate-work": STOCK.homeCraftsman,         // [STOCK – needs BR replacement]
  "window-door-installation": STOCK.homeSunset,   // [STOCK – needs BR replacement]
  "carpentry-framing": BR.framingInterior,        // [BR] interior wood framing
  "exterior-rot-repair": BR.roofGutterChimney,    // [BR-TEMP] roof edge + soffit
  "exterior-painting": STOCK.paintRoller,         // [STOCK – needs BR replacement]
  "water-damage-repairs": STOCK.painterMask,      // [STOCK – needs BR replacement]
  "door-repair": STOCK.homeSunset,                // [STOCK – needs BR replacement]
  "deck-repair": BR.outdoorPoolPergola,           // [BR-TEMP] outdoor living
  "patio-repair": BR.outdoorKitchenBuild,         // [BR] outdoor stonework in progress
  "fence-repair": STOCK.homeCraftsman,            // [STOCK – needs BR replacement]

  // ----- Maintenance & investor -----------------------------------------
  "home-maintenance-plans": BR.roofInspectionWalk, // [BR] roofer on roof inspection look
  "handyman-punch-list": STOCK.caulkGun,           // [STOCK – needs BR replacement]
  "investor-property-repairs": STOCK.homeColonial, // [STOCK – needs BR replacement]
  "make-ready-repairs": STOCK.caulkGun,            // [STOCK – needs BR replacement]
  "rental-property-maintenance": STOCK.homeColonial, // [STOCK – needs BR replacement]
  "turnover-repairs": STOCK.caulkGun,              // [STOCK – needs BR replacement]
};

// Category-level image used by the homepage "What We Do" grid and any other
// places that need a single image per top-level category.
export const CATEGORY_IMAGES = {
  roofing: BR.roofRepairActive,
  remodeling: BR.kitchenWhiteBlue,
  "interior-feature-upgrades": BR.interiorStaircaseFireplace,
  "home-additions": BR.framingInterior,
  "outdoor-living": BR.outdoorPoolPergola,
  "maintenance-plans": BR.roofInspectionWalk,
  "handyman-punch-list": BR.outdoorKitchenBuild,
  "investor-property-repairs": STOCK.homeColonial,
  "subcontractor-partnerships": STOCK.workersHardhat,
};

// Hero-grade images for stand-alone home-page blocks (HomeRoofingBlock,
// HomeRemodelingBlock). Different from CATEGORY_IMAGES so we can use a
// stronger, more cinematic shot on the dedicated section than the small
// service-grid tile.
export const HOMEPAGE_BLOCK_IMAGES = {
  roofing: BR.roofInstallSlate,        // wider scene of an install in progress
  remodeling: BR.kitchenWhiteBlue,     // premium kitchen build
};

export function getServiceImage(slug) {
  return SERVICE_IMAGES[slug] || STOCK.workersHardhat;
}

// Backwards-compatible re-export. Older callers import `IMG` (now BR) to
// hot-link a specific Black Ridge asset. Keep the old name aliased so we
// don't have to touch unrelated files.
export const IMG = { ...BR, ...STOCK };

// Provenance-style export so other modules can introspect what's an
// owner-supplied photo vs. licensed stock when building UI badges.
export const BR_IMAGES = BR;
export const STOCK_IMAGES = STOCK;
